import { Component, Host, h, Prop, Element, State } from '@stencil/core';
import Star2 from "../../assets/svg/star2.svg";
import Helper from '../../Helpers/Helper';

@Component({
  tag: 'salla-rating-stars',
  styleUrl: 'salla-rating-stars.scss'
})
export class SallaRatingStars {
  @State() translationsLoaded: boolean = false;
  @State() labels: string[] = [];
  @State() reviewLabel: string = '';
  @State() selectedStar: number = 0;

  /**
 * Sets input name.
 */
  @Prop() name: string = 'rating';

  /**
  * Sets the height and width of the component. Defaults to medium.
  */
  @Prop() size: "large" | "medium" | "small" | "mini" = 'medium';

  /**
  * The rating value.
  */
  @Prop() value: number;

  /**
 * Number of reviews to display.
 */
  @Prop() reviews: number = 0;

  /**
  * Show the description label.
  */
  @Prop() withLabel: boolean;
  /**
  * Allows the rating to be editable.
  */
  @Prop() editable: boolean = false;

  @Element() host: HTMLElement;
  private starsElem: HTMLElement;
  private reviewsElement: HTMLElement;

  async componentWillLoad() {
    await new Promise<void>(resolve => {
      salla.lang.onLoaded(() => {
        this.labels = [
          salla.lang.get('pages.rating.poor'),
          salla.lang.get('pages.rating.average'),
          salla.lang.get('pages.rating.good'),
          salla.lang.get('pages.rating.very_good'),
          salla.lang.get('pages.rating.excellent')
        ];

        if (this.value && this.withLabel) {
          this.reviewLabel = this.labels[this.value - 1];
        }


        if (this.reviewsElement) {
          this.reviewsElement.innerText = `(${salla.helpers.number(salla.lang.choice('pages.rating.reviews', this.reviews))})`;
        }

        this.translationsLoaded = true;
        resolve();
      });
    });
  }

  protected initiateRating() {
    this.host.addEventListener('click', this.handleRating.bind(this));
  }

  private handleRating() {
    if (!this.starsElem) return;

    let activeStars = this.starsElem.querySelectorAll('.s-rating-stars-hovered');
    let selected = activeStars[activeStars.length - 1];
    if (!selected) return;

    let selectedIndex = parseInt(selected.getAttribute('data-star')!);
    ((this.starsElem as Element).querySelector('.rating_hidden_input') as HTMLInputElement).value = selectedIndex.toString();

    (this.starsElem as Element).querySelectorAll('.s-rating-stars-btn-star')
      .forEach((star, index) => Helper.toggleElementClassIf(star, 's-rating-stars-selected', 's-rating-stars-unselected', () => index < selectedIndex));

    (this.starsElem as Element).querySelectorAll('[aria-pressed]').forEach(star => star.removeAttribute('aria-pressed'));
    selected.setAttribute('aria-pressed', 'true');
    this.selectedStar = selectedIndex;
    this.withLabel && (this.reviewLabel = this.labels[selectedIndex - 1]);
  }

  public triggerRatingProgrammatically(index: number) {
    if (!this.starsElem) return;

    const stars = this.starsElem.querySelectorAll('.s-rating-stars-btn-star');
    if (stars && index >= 0 && index <= stars.length) {
      // Simulate the hovering effect
      stars.forEach((s, i) => {
        s.classList.toggle('s-rating-stars-hovered', i <= index);
      });

      // Trigger the same logic as clicking
      this.handleRating();
    }
  }

  private highlightSelectedStars() {
    let hoveredClass = 's-rating-stars-hovered',
      stars = this.starsElem?.querySelectorAll('.s-rating-stars-btn-star');
    stars?.forEach((star, index) => {
      star.addEventListener('mouseover', () => {
        for (let i = 0; i <= index; i++) {
          stars[i].classList.add(hoveredClass);
        }

        this.withLabel && (this.reviewLabel = this.labels[index]);
      });
      star.addEventListener('mouseout', () => {
        star.classList.remove(hoveredClass);
        this.withLabel && (this.reviewLabel = this.selectedStar ? this.labels[this.selectedStar - 1] : '');
      });
    });
    this.starsElem?.addEventListener('mouseout', () => stars.forEach(star => star.classList.remove(hoveredClass)));
  }

  private createStars(n: number) {
    let stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(<span class={{
        's-rating-stars-btn-star': true,
        ['s-rating-stars-' + this.size]: true,
        's-rating-stars-selected': i < n
      }} innerHTML={Star2} />);
    }
    if (this.reviews > 0) {
      stars.push(<span class="s-rating-stars-reviews" ref={el => this.reviewsElement = el}>
        ({salla.helpers.number(salla.lang.choice('pages.rating.reviews', this.reviews))})
      </span>);
    }
    return stars;
  }

  render() {
    return this.translationsLoaded ? (
      this.host.closest('.swiper-slide')?.classList.contains('swiper-slide-duplicate')
        ? ''
        : (
          <Host>
            {(this.value || this.value == 0) && !this.editable ?
              <div class="s-rating-stars-wrapper">
                {this.createStars(this.value)}
                {this.withLabel && this.reviewLabel ? <span class="s-rating-stars-label">{this.reviewLabel}</span> : ''}
              </div>
              :
              <div class="s-rating-stars-wrapper">
                <div class="s-rating-stars-element" ref={(el) => this.starsElem = el as HTMLElement}>
                  <input type="hidden" class="rating_hidden_input" name={this.name} value="" />
                  {[1, 2, 3, 4, 5].map(star =>
                    <button class={`s-rating-stars-btn-star s-rating-stars-` + this.size} data-star={star}>
                      <span innerHTML={Star2} />
                    </button>
                  )}
                </div>
                {this.withLabel && this.reviewLabel ? <span class="s-rating-stars-label">{this.reviewLabel}</span> : ''}
              </div>
            }
          </Host>
        )
    ) : (
      <Host></Host>
    );
  }

  componentDidLoad() {
    this.initiateRating();
    this.highlightSelectedStars();

    if (this.value && this.editable) {
      const stars = this.starsElem?.querySelectorAll('.s-rating-stars-btn-star');
      if (stars && this.value >= 0 && this.value <= stars.length) {
        this.triggerRatingProgrammatically(this.value - 1);
      }
    }

  }

}